import { useCallback, useEffect, useState } from 'react';

import type {
  CompletionStats,
  CostStats,
  DateRange,
  DurationStats,
  EndpointStats,
  ErrorCategory,
  HistogramBucket,
  StatsData,
} from '../../types';
import { listAllHistoryJobs } from '../api';
import type { FalEndpointUsage, Job } from '../types';
import { jobEndpointCosts } from '../utils/falPricing';

/**
 * Stats computation for the 360 video service.
 *
 * Produces the same `StatsData` shape lightning's stats components
 * consume, so `web/src/components/stats/*` can be reused 1:1 in the
 * video Statistics page. The compute logic mirrors lightning's
 * `useStats` but is simplified for the video data model:
 *
 *  - Cost isn't recomputed from a `pricing_settings` table at display
 *    time: the backend prices each call as it runs and persists the
 *    total (see `calculateVideoJobCost`).
 *  - There's no Gemini provider, so all endpoints in
 *    `EndpointStats` have `provider === 'fal'`.
 *  - `cancelled` doesn't exist as a status in the video service
 *    (aborts are recorded as `failed` with the "Aborted by user"
 *    error message). The lightning `CompletionStats` shape still
 *    reserves a `cancelled` slot — we always set it to 0 so the
 *    components render cleanly.
 *  - There are no service-version comparison rows (video always runs
 *    a single image), so `versions` and `versionStats` are empty.
 */

// ---------- shared math helpers (mirrored from useStats) -------------------

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

function createHistogram(
  values: number[],
  bucketCount = 10,
  formatLabel: (start: number, end: number) => string = (s, e) =>
    `${s.toFixed(1)}-${e.toFixed(1)}`,
): HistogramBucket[] {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const bucketSize = range / bucketCount || 1;

  const buckets: HistogramBucket[] = [];
  for (let i = 0; i < bucketCount; i++) {
    const rangeStart = min + i * bucketSize;
    const rangeEnd = min + (i + 1) * bucketSize;
    buckets.push({
      range: formatLabel(rangeStart, rangeEnd),
      rangeStart,
      rangeEnd,
      count: 0,
    });
  }

  for (const value of values) {
    const idx = Math.min(
      Math.floor((value - min) / bucketSize),
      bucketCount - 1,
    );
    if (idx >= 0 && idx < buckets.length) buckets[idx].count++;
  }
  return buckets;
}

function formatDurationLabel(start: number, end: number): string {
  const formatSec = (s: number) => {
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return sec > 0 ? `${m}m${sec}s` : `${m}m`;
  };
  return `${formatSec(start)}-${formatSec(end)}`;
}

function formatCostLabel(start: number, end: number): string {
  return `$${start.toFixed(3)}-$${end.toFixed(3)}`;
}

// ---------- per-job cost ---------------------------------------------------

/**
 * Sum of cost across every FAL endpoint billed for this job.
 *
 * Uses FAL's `direct_cost` when the endpoint reported one, then the
 * `priced_cost` the backend computed and persisted while the job ran —
 * the same figures its `generation_log.csv` holds, which is what makes
 * the two agree — and only then the static table in `utils/falPricing`,
 * for jobs older than that. Endpoints nothing can price contribute $0
 * and show in the History tooltip as `(estimate unavailable)`.
 */
export function calculateVideoJobCost(job: Job): number {
  return jobEndpointCosts(job).reduce((sum, ec) => sum + ec.cost, 0);
}

// ---------- error categorisation (same buckets as lightning) ---------------

function categorizeError(errorMessage: string | null | undefined): string {
  if (!errorMessage) return 'Unknown';
  const msg = errorMessage.toLowerCase();
  if (msg.includes('aborted by user')) return 'Aborted';
  if (msg.includes('timeout') || msg.includes('timed out')) return 'Timeout';
  if (
    msg.includes('rate limit') ||
    msg.includes('429') ||
    msg.includes('quota')
  ) {
    return 'Rate Limited';
  }
  if (msg.includes('missing required merch')) return 'Missing Merch';
  if (
    msg.includes('invalid') ||
    msg.includes('not found') ||
    msg.includes('404')
  ) {
    return 'Invalid Scan';
  }
  if (
    msg.includes('vertex') ||
    msg.includes('gemini') ||
    msg.includes('generation') ||
    msg.includes('fal')
  ) {
    return 'AI Generation Error';
  }
  if (
    msg.includes('api') ||
    msg.includes('fetch') ||
    msg.includes('network') ||
    msg.includes('connection')
  ) {
    return 'API Error';
  }
  return 'Other';
}

// ---------- per-stat computations -----------------------------------------

function computeDuration(jobs: Job[]): DurationStats {
  const durations = jobs
    .filter((j) => j.status === 'completed' && j.duration_seconds != null)
    .map((j) => j.duration_seconds as number);

  if (durations.length === 0) {
    return { average: 0, median: 0, max: 0, min: 0, histogram: [] };
  }
  return {
    average: durations.reduce((a, b) => a + b, 0) / durations.length,
    median: median(durations),
    max: Math.max(...durations),
    min: Math.min(...durations),
    histogram: createHistogram(durations, 8, formatDurationLabel),
  };
}

function computeCost(jobs: Job[]): CostStats {
  const completed = jobs.filter((j) => j.status === 'completed');
  const costs = completed.map(calculateVideoJobCost);
  const nonZero = costs.filter((c) => c > 0);

  if (nonZero.length === 0) {
    return { average: 0, median: 0, max: 0, min: 0, total: 0, histogram: [] };
  }
  const total = costs.reduce((a, b) => a + b, 0);
  return {
    average: total / completed.length,
    median: median(nonZero),
    max: Math.max(...nonZero),
    min: Math.min(...nonZero),
    total,
    histogram: createHistogram(nonZero, 8, formatCostLabel),
  };
}

function computeCompletion(jobs: Job[]): CompletionStats {
  const completed = jobs.filter((j) => j.status === 'completed').length;
  const failed = jobs.filter((j) => j.status === 'failed').length;
  const total = jobs.length;
  const relevant = completed + failed;
  return {
    completed,
    failed,
    cancelled: 0, // video service doesn't have a `cancelled` status
    total,
    completedPercent: relevant > 0 ? (completed / relevant) * 100 : 0,
    failedPercent: relevant > 0 ? (failed / relevant) * 100 : 0,
    cancelledPercent: 0,
  };
}

function computeErrors(jobs: Job[]): ErrorCategory[] {
  const failed = jobs.filter((j) => j.status === 'failed');
  if (failed.length === 0) return [];

  const counts: Record<string, number> = {};
  for (const job of failed) {
    const cat = categorizeError(job.error);
    counts[cat] = (counts[cat] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([category, count]) => ({
      category,
      count,
      percent: (count / failed.length) * 100,
    }))
    .sort((a, b) => b.count - a.count);
}

function computeEndpoints(jobs: Job[]): EndpointStats[] {
  type Acc = {
    totalCalls: number;
    jobsCompleted: number;
    jobsFailed: number;
    durationShareSum: number;
    durationShareCalls: number;
  };
  const map = new Map<string, Acc>();
  const ensure = (key: string): Acc => {
    let entry = map.get(key);
    if (!entry) {
      entry = {
        totalCalls: 0,
        jobsCompleted: 0,
        jobsFailed: 0,
        durationShareSum: 0,
        durationShareCalls: 0,
      };
      map.set(key, entry);
    }
    return entry;
  };

  for (const job of jobs) {
    if (!job.fal_usage) continue;
    const isCompleted = job.status === 'completed';
    const totalJobCalls = Object.values(job.fal_usage).reduce(
      (sum, u) => sum + ((u as FalEndpointUsage).calls ?? 0),
      0,
    );

    for (const [endpoint, usage] of Object.entries(job.fal_usage)) {
      const calls = (usage as FalEndpointUsage).calls ?? 0;
      const entry = ensure(endpoint);
      entry.totalCalls += calls;
      if (isCompleted) {
        entry.jobsCompleted++;
        if (job.duration_seconds != null && totalJobCalls > 0) {
          entry.durationShareSum +=
            (calls / totalJobCalls) * job.duration_seconds;
          entry.durationShareCalls += calls;
        }
      } else if (job.status === 'failed') {
        entry.jobsFailed++;
      }
    }
  }

  return Array.from(map.entries())
    .map(([endpoint, data]) => {
      const totalJobs = data.jobsCompleted + data.jobsFailed;
      return {
        endpoint,
        provider: 'fal' as const,
        totalCalls: data.totalCalls,
        jobsCompleted: data.jobsCompleted,
        jobsFailed: data.jobsFailed,
        successRate:
          totalJobs > 0 ? (data.jobsCompleted / totalJobs) * 100 : 0,
        avgDuration:
          data.durationShareCalls > 0
            ? data.durationShareSum / data.durationShareCalls
            : 0,
      };
    })
    .sort((a, b) => b.totalCalls - a.totalCalls);
}

function computeAll(jobs: Job[], dateRange: DateRange): StatsData {
  return {
    duration: computeDuration(jobs),
    cost: computeCost(jobs),
    completion: computeCompletion(jobs),
    errors: computeErrors(jobs),
    endpointStats: computeEndpoints(jobs),
    versions: [],
    versionStats: [],
    totalJobs: jobs.length,
    dateRange,
  };
}

// ---------- hook -----------------------------------------------------------

export function useVideoStats(dateRange: DateRange) {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!dateRange.from && !dateRange.to) {
      setStats(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Statistics are summary aggregates, so the whole window is needed —
      // not just the page the history table happens to show.
      const jobs = await listAllHistoryJobs(dateRange);
      setStats(computeAll(jobs, dateRange));
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Failed to fetch statistics');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}

// ---------- date-range presets (same shape as lightning's helper) ---------

export type DateRangePreset = 'today' | 'last7days' | 'last30days' | 'custom';

export function getDateRangeFromPreset(preset: DateRangePreset): DateRange {
  const now = new Date();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);

  switch (preset) {
    case 'today': {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'custom':
    default:
      return { from: null, to: null };
  }
}
